import loadable from 'react-loadable';
import type { ComponentType } from 'react';

import { MilanoteApplicationMode } from '../../../../common/platform/platformTypes';
import { COLLAPSE_ORDER, TOOL_HEIGHT } from '../../../workspace/toolbar/config/toolbarToolConfigConstants';
import type { ToolbarItemConfig } from '../../../workspace/toolbar/toolbarTypes';

// eslint-disable-next-line no-restricted-imports
const importTables = () => import(/* webpackChunkName: 'tables' */ '../module/tools');
const loadTableTool = (tool: keyof typeof import('../module/tools')) =>
    loadable({
        loader: () => importTables().then((tools) => tools[tool] as ComponentType),
        loading: () => null,
    });

const TABLE_CELL_ALIGNMENT: ToolbarItemConfig = {
    id: 'TABLE_CELL_ALIGNMENT',
    name: 'Alignment',
    localeKey: 'tools.editing.table.alignment',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.DEFAULT,
    component: loadTableTool('TableCellAlignmentTool'),
    mobileComponent: loadTableTool('MobileTableCellAlignmentTool'),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_TEXT_STYLES: ToolbarItemConfig = {
    id: 'TABLE_TEXT_STYLES',
    name: 'Text style',
    localeKey: 'tools.editing.textStyle',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.LATE,
    component: loadTableTool('TableTextStyleTool'),
    mobileComponent: loadTableTool('MobileTableTextStyleRichTextTool'),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_CELL_FORMULA: ToolbarItemConfig = {
    id: 'TABLE_CELL_FORMULA',
    name: 'Formula',
    localeKey: 'tools.editing.table.formula',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.DEFAULT,
    component: loadTableTool('TableCellFormulaTool'),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_CELL_TYPE: ToolbarItemConfig = {
    id: 'TABLE_CELL_TYPE',
    name: 'Cell type',
    localeKey: 'tools.editing.table.cellType',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.NEVER,
    component: loadTableTool('CellTypeTool'),
    mobileComponent: loadTableTool('MobileCellTypeTool'),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_CELL_TYPE_FORMAT: ToolbarItemConfig = {
    id: 'TABLE_CELL_TYPE_FORMAT',
    name: 'Format',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.NEVER,
    component: loadTableTool('MobileCellTypeFormatTool'),
    appModeAvailability: [MilanoteApplicationMode.mobile],
};

const TABLE_CELL_COLOR: ToolbarItemConfig = {
    id: 'TABLE_CELL_COLOR',
    name: 'Cell Color',
    localeKey: 'tools.editing.table.cellColor',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.NEVER,
    component: loadTableTool('TableCellColorTool'),
    mobileComponent: loadTableTool('MobileTableCellColorTool'),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_ADD_COLUMN: ToolbarItemConfig = {
    id: 'TABLE_ADD_COLUMN',
    name: 'Add column',
    localeKey: 'tools.editing.table.addColumn',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.DEFAULT,
    component: loadTableTool('AddColumnTool'),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_ADD_ROW: ToolbarItemConfig = {
    id: 'TABLE_ADD_ROW',
    name: 'Add row',
    localeKey: 'tools.editing.table.addRow',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.DEFAULT,
    component: loadTableTool('AddRowTool'),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_CONTEXT_MENU: ToolbarItemConfig = {
    id: 'TABLE_CONTEXT_MENU',
    name: 'More',
    localeKey: 'tools.selected.more',
    height: {
        large: 16,
        medium: 16,
        small: 14,
    },
    collapseOrder: COLLAPSE_ORDER.EARLY,
    collapseBehavior: 'hide',
    component: loadTableTool('TableContextMenuTool'),
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
    mobileComponent: loadTableTool('MobileTableContextMenuTool'),
};

export default {
    TABLE_CELL_ALIGNMENT,
    TABLE_TEXT_STYLES,
    TABLE_CELL_FORMULA,
    TABLE_CELL_TYPE,
    TABLE_CELL_TYPE_FORMAT,
    TABLE_CELL_COLOR,
    TABLE_ADD_COLUMN,
    TABLE_ADD_ROW,
    TABLE_CONTEXT_MENU,
} as const;
